import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import MarkdownMessage from '../ai/MarkdownMessage';
import BotAvatar from './BotAvatar';
import RunSteps from './RunSteps';
import AnswerOutputs from './AnswerOutputs';
import { RUNNING, activityLabel } from './botActivity';
import { runOutputs } from '../../lib/answerParts';
import { useRole } from '../../hooks/useRole';

export const Dots = () => (
  <span className="inline-flex gap-1 py-1" aria-hidden="true">
    {[0, 0.1, 0.2].map((d) => (
      <span key={d} className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />
    ))}
  </span>
);

const elapsed = (from, now) => {
  const secs = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
};

// After this long without any news from a run (no text, no step), the person is told: a
// model that thinks this long is rare, a stalled call looks the same from outside, and a bot
// that seemed to work for minutes with nothing happening was reported as "pretending".
const NO_NEWS_SECONDS = 120;

/** "Nothing new for 3 min": shown under a working bot once it has been silent that long. */
const NoNews = ({ lastAt }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);
  const quiet = lastAt ? (now - new Date(lastAt).getTime()) / 1000 : 0;
  if (quiet < NO_NEWS_SECONDS) return null;
  return (
    <span className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="bot-no-news">
      <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
      {t('bots.chat.no_news', { minutes: Math.floor(quiet / 60) })}
    </span>
  );
};

/**
 * What the bot is doing right now, small and quiet like OpenMausBot's presence: its
 * face breathing, the step in words and the time so far. A click shows the steps.
 */
const LiveLine = ({ bot, label, since, open, onToggle }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid="bot-working"
      className="inline-flex items-center gap-2 py-0.5 pl-1 text-xs text-muted-foreground/80 hover:text-muted-foreground">
      <BotAvatar bot={bot} size="xs" state="working" />
      <span>{label}</span>
      {since && <span className="tabular-nums text-muted-foreground/60">· {elapsed(since, now)}</span>}
    </button>
  );
};

const ENDED = { failed: 'bots.chat.failed_hint', cancelled: 'bots.chat.cancelled_hint' };
// A model provider that refused the work, as a code (backend utils/ai_errors.py): never its own words.
const AI_ERRORS = new Set(['ai_unavailable', 'own_key_no_credit', 'own_key_refused']);
const KNOWN_ERRORS = new Set(['credits_exhausted', 'paused', 'project_budget', ...AI_ERRORS]);
const errorKey = (status, error) => (
  error === 'credits_exhausted' ? 'bots.credits.exhausted_run'
    : error === 'paused' ? 'bots.paused.stopped'
      : error === 'project_budget' ? 'bots.paused.budget_stopped'
        : AI_ERRORS.has(error) ? `errors.${error}`
          : ENDED[status] || 'bots.chat.failed_hint'
);

/**
 * A bot's answer in a conversation (a chat, or a task's thread): what it did, what it
 * wrote, and how the run ended. The answer is one stored message from the moment the
 * run is queued (backend agents/conversation.py), so it keeps its place and never
 * disappears: while the bot works it shows the live text (or what was saved of it),
 * afterwards the stored text, and a failed or stopped run says so in the same place.
 *
 * message: the stored assistant message (null for runs from before stored replies).
 * run: its run (status, steps). live: the run's live events (hooks/useBotRunStreams).
 * cards: the run's decided cards; pendingCards: the ones waiting for the person.
 * Once it is done, the documents it wrote and the files it saved show as cards under it.
 * A run that failed on an error shows the error itself to the app's admins, so whoever
 * runs BusyBee.Day can tell what happened (everyone else sees the hint only).
 * bubble: the chat's look (Rakazo, OpenMausBot): the text in a tinted bubble and, while
 * the bot works, one small line with its latest steps under it instead of the full step
 * list (bot: whose face it shows); a click on the line shows every step.
 * Its parts (steps, cards, text, files, the line under it) are one column with one gap,
 * so any of them can be missing or come in any mix without two of them touching.
 */
const BotReply = ({ message, run, live, onOpenTask, onDownload, cards, pendingCards, onRetry, retrying, canRetry, bubble, bot }) => {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const [showSteps, setShowSteps] = useState(false);
  const status = run?.status || message?.status || 'done';
  const running = RUNNING.has(status);
  const steps = running && live?.steps?.length ? live.steps : (run?.actions || []);
  const text = running ? (live?.text || message?.content || '') : (message?.content || '');
  const ended = status === 'failed' || status === 'cancelled';
  const error = run?.error || message?.error;
  // Why the work ended before the task was done (backend agents/runtime.py): the bot got
  // stuck in a loop, or, in runs from before 2026-10-19, a step or time limit. A run the
  // person stopped at a check-in says so in its card.
  const stoppedBy = status === 'done' ? (live?.stoppedBy || run?.stopped_by) : null;
  const stopKey = { stuck: 'bots.chat.stopped_stuck', steps: 'bots.chat.stopped_steps', time: 'bots.chat.stopped_time' }[stoppedBy];
  const quiet = bubble && running;
  return (
    <div className="flex flex-col gap-2.5 min-w-0" data-testid="bot-reply">
      {!quiet && (
        <RunSteps steps={steps} said={run?.narration} running={running} ended={ended || !!stopKey} startedAt={run?.created_at}
          finishedAt={running ? null : run?.finished_at} onOpenTask={onOpenTask} onDownload={onDownload} />
      )}
      {cards}
      {text && (bubble ? (
        <div className="w-fit max-w-[min(100%,620px)] rounded-[18px] rounded-bl-md bg-bubble-bot px-4 py-3"
          data-testid="bot-bubble">
          <MarkdownMessage textClass="text-[14px] leading-[1.65] max-md:text-[15px]">{text}</MarkdownMessage>
        </div>
      ) : <MarkdownMessage>{text}</MarkdownMessage>)}
      {!running && <AnswerOutputs outputs={runOutputs(run?.actions)} onDownload={onDownload} />}
      {quiet && (
        <div>
          <LiveLine bot={bot} label={activityLabel(t, live)} since={run?.created_at} open={showSteps}
            onToggle={() => setShowSteps((o) => !o)} />
          <RunSteps steps={steps} running listOnly latest={!showSteps} onOpenTask={onOpenTask} onDownload={onDownload} />
          <NoNews lastAt={live?.lastAt || run?.started_at || run?.created_at} />
        </div>
      )}
      {running && !bubble && (!text || live?.tool) && (
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="bot-working">
          <Dots />{activityLabel(t, live)}
        </span>
      )}
      {running && !bubble && <NoNews lastAt={live?.lastAt || run?.started_at || run?.created_at} />}
      {status === 'waiting_approval' && pendingCards}
      {stopKey && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="bot-reply-stopped">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <span className="min-w-0">{t(stopKey)}</span>
        </div>
      )}
      {ended && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5" data-testid="bot-reply-ended">
              <span>{t(errorKey(status, error))}</span>
              {canRetry && onRetry && error !== 'credits_exhausted' && (
                <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={onRetry} disabled={retrying}>
                  <RotateCcw className="w-3.5 h-3.5" />{t('bots.chat.retry')}
                </Button>
              )}
            </div>
            {isAdmin && error && !KNOWN_ERRORS.has(error) && (
              <p className="text-xs font-mono break-words" data-testid="bot-reply-error">{error}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BotReply;
